// SamplingInformationSection.js
// This component handles the Sample Information section of the Wells tab

import React, { useState, useEffect } from "react";
import Select from "react-select";
// Constants
import { SAMPLE_ODOR_OPTIONS } from "../../../../../constants/samplingEventConstants";
// Components
import { SamplingEventDatePicker } from "../../../common/SamplingEventDatePicker";
import { SamplingEventTimePickerInput } from "../../../common/SamplingEventTimePickerInput";
import styles from "./SamplingInformationSection.module.css";

// Main Sample Information Section Component
export const SamplingInformationSection = ({
  well,
  wellActions,
  emitPatch,
}) => {
  // Local state for the unable to collect sample draft
  const [unableToCollectSampleDraft, setUnableToCollectSampleDraft] = useState(
    well.unableToCollectSample
  );

  // Handle time change for the sample time field
  const handleTextInputChange = (field, value) => {
    wellActions.updateWellSample(well.id, [{ field, value }]);
  };

  // Handle text input blur events (send update to server)
  const handleTextInputBlur = (field, value) => {
    wellActions.updateWellSample(well.id, [{ field, value }], emitPatch);
  };

  // Handle checkbox change for unable to collect sample field
  const handleCheckboxChange = (e) => {
    wellActions.updateWellSample(
      well.id,
      [{ field: "unableToCollectSample", value: e.target.checked }],
      emitPatch
    );
    setUnableToCollectSampleDraft(e.target.checked);
  };

  // Handle select changes for odor field (send immediately)
  const handleSelectChange = (selectedOption) => {
    const value = selectedOption ? selectedOption.value : "";
    wellActions.updateWellSample(
      well.id,
      [{ field: "sampleOdor", value }],
      emitPatch
    );
  };

  // Helper function to get the currently selected option for a field
  const getSelectedOption = (options, value) => {
    if (!value) return null;
    return options.find((option) => option.value === value) || null;
  };

  // Handle date change for the sample date field
  const handleDateChange = (date) => {
    wellActions.updateWellSample(
      well.id,
      [{ field: "sampleDate", value: date }],
      emitPatch
    );
  };

  // Handle time change for the sample time field
  const handleTimeChange = (time) => {
    wellActions.updateWellSample(
      well.id,
      [{ field: "sampleTime", value: time }],
      emitPatch
    );
  };

  return (
    <div className={styles.samplingInformationSection}>
      <div className={styles.sectionHeader}>
        <h4>5. Sample Information</h4>
      </div>

      {/* Row 1: Unable to Collect Sample */}
      <div className={styles.sampleInfoRow}>
        <div className={styles.sampleFieldCheckbox}>
          <label htmlFor={`unableToCollectSample-${well.id}`}>
            Unable to Collect Sample:
          </label>
          <input
            type="checkbox"
            id={`unableToCollectSample-${well.id}`}
            name="unableToCollectSample"
            checked={well.unableToCollectSample}
            onChange={handleCheckboxChange}
          />
        </div>
      </div>
      {unableToCollectSampleDraft && (
        <div className={styles.sampleInfoRow}>
          <div className={styles.sampleFieldUnableToCollectSampleReason}>
            <label htmlFor={`unableToCollectSampleReason-${well.id}`}>
              Unable to Collect Sample Reason:
            </label>
            <textarea
              id={`unableToCollectSampleReason-${well.id}`}
              name="unableToCollectSampleReason"
              value={well.unableToCollectSampleReason}
              onChange={(e) =>
                handleTextInputChange(
                  "unableToCollectSampleReason",
                  e.target.value
                )
              }
              onBlur={() =>
                handleTextInputBlur(
                  "unableToCollectSampleReason",
                  well.unableToCollectSampleReason
                )
              }
              rows={3}
              className={styles.formInput}
              placeholder="Reason for inability to collect sample"
            />
          </div>
        </div>
      )}

      {/* Row 2: Sample Time and QC Samples */}
      <div className={styles.sampleInfoRow}>
        <div className={styles.datePickerField}>
          <label htmlFor={`sampleDate-${well.id}`}>Sample Date:</label>
          <SamplingEventDatePicker
            id={`sampleDate-${well.id}`}
            name="sampleDate"
            selected={well.sampleDate || null}
            onChange={(date) => {
              handleDateChange(date);
            }}
            className={styles.formInput}
            dateFormat="MM/dd/yyyy"
            isClearable={true}
            placeholderText="Select date"
          />
        </div>
        <div className={styles.timePickerField}>
          <label htmlFor={`sampleTime-${well.id}`}>Sample Time:</label>
          <SamplingEventTimePickerInput
            value={well.sampleTime || ""}
            onChange={(time) => {
              handleTextInputChange("sampleTime", time);
            }}
            onBlur={(e) => handleTextInputBlur("sampleTime", e.target.value)}
            id={`sampleTime-${well.id}`}
          />
        </div>
        <div className={styles.qcSamplesField}>
          <label htmlFor={`qcSamples-${well.id}`}>QC Samples:</label>
          <input
            type="text"
            id={`qcSamples-${well.id}`}
            name="qcSamples"
            value={well.qcSamples || ""}
            onChange={(e) => handleTextInputChange("qcSamples", e.target.value)}
            onBlur={() => handleTextInputBlur("qcSamples", well.qcSamples)}
            className={styles.formInput}
            placeholder="QC Sample information"
          />
        </div>
      </div>

      {/* Row 3: Sample Appearance, Sample Color, and Odor */}
      <div className={styles.sampleInfoRow}>
        <div className={styles.sampleField}>
          <label htmlFor={`sampleAppearance-${well.id}`}>
            Sample Appearance:
          </label>
          <input
            type="text"
            id={`sampleAppearance-${well.id}`}
            name="sampleAppearance"
            value={well.sampleAppearance || ""}
            onChange={(e) =>
              handleTextInputChange("sampleAppearance", e.target.value)
            }
            onBlur={() =>
              handleTextInputBlur("sampleAppearance", well.sampleAppearance)
            }
            className={styles.formInput}
            placeholder="Appearance"
          />
        </div>
        <div className={styles.sampleField}>
          <label htmlFor={`sampleColor-${well.id}`}>Sample Color:</label>
          <input
            type="text"
            id={`sampleColor-${well.id}`}
            name="sampleColor"
            value={well.sampleColor || ""}
            onChange={(e) =>
              handleTextInputChange("sampleColor", e.target.value)
            }
            onBlur={() => handleTextInputBlur("sampleColor", well.sampleColor)}
            className={styles.formInput}
            placeholder="Color"
          />
        </div>
        <div className={styles.sampleField}>
          <label htmlFor={`sampleOdor-${well.id}`}>Odor:</label>
          <Select
            id={`sampleOdor-${well.id}`}
            value={getSelectedOption(SAMPLE_ODOR_OPTIONS, well.sampleOdor)}
            onChange={handleSelectChange}
            options={SAMPLE_ODOR_OPTIONS}
            className={styles.selectControl}
            classNamePrefix="select"
            placeholder="Select"
            isClearable
          />
        </div>
      </div>

      {/* Row 4: Sample Comments */}
      <div className={styles.commentsRow}>
        <div className={styles.commentsField}>
          <label htmlFor={`sampleComments-${well.id}`}>Sample Comments:</label>
          <textarea
            id={`sampleComments-${well.id}`}
            name="sampleComments"
            value={well.sampleComments || ""}
            onChange={(e) =>
              handleTextInputChange("sampleComments", e.target.value)
            }
            onBlur={() =>
              handleTextInputBlur("sampleComments", well.sampleComments)
            }
            className={styles.commentsTextarea}
            placeholder="Additional sample comments"
            rows={3}
          />
        </div>
      </div>
    </div>
  );
};
